/**
 * HLOS-168-B + HLOS-172: Vault backend "capacitor-ios" — delega para o
 * `IOSVaultPlugin.swift` (HLOS-168-A) via `window.webkit.messageHandlers.hlos`.
 *
 * Bridge APENAS transporta bytes JÁ cifrados (vault-crypto skill).
 * Master key nunca trafega por este canal (CL-08 zero key escrow).
 *
 * HLOS-172 — Path conventions:
 *   TS-side (`paths.vaultFilePath`) retorna path completo desde app sandbox:
 *     "hlos/vault/hlos_primary/vault.json"
 *   Swift-side (`IOSVaultPlugin.absoluteURL`) resolve o relativePath ADICIONANDO
 *     o `vaultRootDir = "hlos/vault"`.
 *
 *   SEM stripVaultRoot: TS envia "hlos/vault/hlos_primary/vault.json" → Swift
 *   resolve para "Documents/hlos/vault/hlos/vault/hlos_primary/vault.json"
 *   (repetição do prefixo) → vault fica em path errado, nunca encontrado.
 *   Resultado: usuário precisa entrar passphrase toda vez (vault existe no path
 *   errado, vaultExists() no próximo boot retorna false → cria vault novo,
 *   perde conversa anterior).
 *
 *   COM stripVaultRoot: TS envia "hlos/vault/hlos_primary/vault.json" → strip
 *   vira "hlos_primary/vault.json" → Swift resolve para
 *   "Documents/hlos/vault/hlos_primary/vault.json" ✓
 *
 * Logs estruturados via HLOS-169 (correlation_id + sanitized keywords).
 */

import type { VaultStorage } from "../store";
import { VAULT_ROOT_DIR } from "../paths";
import {
  installIOSVaultBridge,
  isIOSVaultBridgeAvailable,
  type IOSVaultBridge,
} from "../../capacitor/vault_bridge";

// Re-export para testes (HLOS-172 — type-only re-export).
export type { IOSVaultBridge };

/**
 * HLOS-172: Strip o prefixo "hlos/vault/" do path antes de enviar para o Swift.
 *
 * Convenção:
 *   - input:  "hlos/vault/<resto>"  (formato TS, completo desde sandbox root)
 *   - output: "<resto>"            (formato Swift, relativo a vaultRootDir)
 *
 * Idempotente: se o path já é relativo (sem prefixo), retorna como está.
 *
 * Path traversal: o `IOSVaultPlugin.absoluteURL` Swift-side já valida
 * contra ".." e prefixos absolutos. Aqui apenas stripamos o prefixo do
 * TS, mantendo a checagem Swift como barreira final.
 *
 * @internal Exportado apenas para testes de regressão.
 */
export function stripVaultRoot(path: string): string {
  const prefix = `${VAULT_ROOT_DIR}/`; // "hlos/vault/"
  return path.startsWith(prefix) ? path.slice(prefix.length) : path;
}

/**
 * Backend iOS que delega para Swift nativo via postMessage.
 *
 * Instalação: lazy. Só carrega o bridge na primeira operação de I/O.
 * Se `installIOSVaultBridge()` retornar null (não-iOS, sem handler),
 * operações falham com `VaultIOSBridgeUnavailableError`.
 *
 * Migração de OPFS legacy: o `VaultStore` chama `migrateFromOPFS` uma vez
 * no primeiro boot iOS (HLOS-168-B integration). Aqui apenas delega.
 *
 * HLOS-172: TODA operação de I/O aplica `stripVaultRoot()` no path antes
 * de enviar ao bridge. Ver doc do header para rationale.
 */
export class CapacitorIOSBackend implements VaultStorage {
  private bridge: IOSVaultBridge | null = null;
  private triedInstall = false;

  /**
   * @param testBridge Opcional — bridge injetado para testes (HLOS-172). Em produção,
   *   `installIOSVaultBridge()` é chamado lazy no primeiro uso.
   */
  constructor(testBridge?: IOSVaultBridge) {
    if (testBridge) {
      this.bridge = testBridge;
      this.triedInstall = true;
    }
  }

  /** Lazy install — só tenta uma vez. */
  private async ensureBridge(): Promise<IOSVaultBridge> {
    if (this.bridge) return this.bridge;
    if (!this.triedInstall) {
      this.triedInstall = true;
      this.bridge = installIOSVaultBridge();
    }
    if (!this.bridge) {
      throw new VaultIOSBridgeUnavailableError(
        "IOSVaultBridge not available (not iOS native or handler missing)",
      );
    }
    return this.bridge;
  }

  async read(relativePath: string): Promise<Uint8Array | null> {
    const bridge = await this.ensureBridge();
    const swiftPath = stripVaultRoot(relativePath);
    // HLOS-172 (diagnóstico): logar path antes/depois do strip + tamanho do retorno.
    console.info(
      "[HLOS][vault-ios] read: original=%s swiftPath=%s",
      relativePath,
      swiftPath,
    );
    const b64 = await bridge.read(swiftPath);
    console.info(
      "[HLOS][vault-ios] read: bridge returned %s",
      b64 === null ? "null (Swift says file NOT found)" : `${b64.length} chars base64`,
    );
    if (b64 === null) return null;
    // base64 → Uint8Array (browser + node Web API).
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  async write(relativePath: string, data: Uint8Array): Promise<void> {
    const bridge = await this.ensureBridge();
    // Uint8Array → base64 (browser + node Web API).
    let binary = "";
    for (let i = 0; i < data.length; i++) {
      binary += String.fromCharCode(data[i]!);
    }
    const b64 = btoa(binary);
    const swiftPath = stripVaultRoot(relativePath);
    const ok = await bridge.write(swiftPath, b64);
    console.info(
      "[HLOS][vault-ios] write: original=%s swiftPath=%s ok=%s",
      relativePath,
      swiftPath,
      ok,
    );
    if (!ok) {
      throw new Error(`CapacitorIOSBackend.write: Swift returned false for "${swiftPath}"`);
    }
  }

  async list(directory: string): Promise<string[]> {
    const bridge = await this.ensureBridge();
    // Se vazio, Swift usa vaultRootDir como base; senão, stripVaultRoot
    // garante path relativo correto.
    const swiftDir = directory === "" ? "" : stripVaultRoot(directory);
    return bridge.list(swiftDir);
  }

  async remove(_relativePath: string): Promise<void> {
    // Swift não tem handler "vault.remove" ainda — chamamos list() e verificamos.
    // Para simplicidade no HLOS-168-B, raise error: remoção é raro e pode
    // ser adicionada em HLOS-168-B.1 (housekeeping).
    const bridge = await this.ensureBridge();
    void bridge;
    throw new Error(
      `CapacitorIOSBackend.remove: not implemented yet (HLOS-168-B); ` +
        `use OPFSBackend for removal until Swift handler is added.`,
    );
  }

  /** Migração one-shot: vault OPFS → Documents/ */
  async migrateFromOPFS(
    userId: string,
    opfsRelativePath: string,
    content: Uint8Array,
  ): Promise<void> {
    const bridge = await this.ensureBridge();
    let binary = "";
    for (let i = 0; i < content.length; i++) {
      binary += String.fromCharCode(content[i]!);
    }
    const b64 = btoa(binary);
    // HLOS-172: opfsRelativePath originalmente é "hlos/vault/<userId>/<resto>"
    // (formato OPFS). StripVaultRoot vira "<resto>", que é o path relativo
    // que o Swift espera (relativo a vaultRootDir).
    const swiftOpfsPath = stripVaultRoot(opfsRelativePath);
    const ok = await bridge.migrateFromOPFS(userId, swiftOpfsPath, b64);
    if (!ok) {
      throw new Error(
        `CapacitorIOSBackend.migrateFromOPFS: Swift returned false ` +
          `(userId=${userId}, opfsPath=${swiftOpfsPath})`,
      );
    }
  }
}

/** Erro lançado quando o bridge iOS não está disponível (não-iOS ou handler ausente). */
export class VaultIOSBridgeUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "VaultIOSBridgeUnavailableError";
  }
}

/** Helper: true se o backend iOS nativo está disponível no runtime atual. */
export function isCapacitorIOSAvailable(): boolean {
  return isIOSVaultBridgeAvailable();
}

/**
 * HLOS-172 FIX v4: aguarda Swift WKScriptMessageHandler registrar.
 *
 * Race condition no cold start: `AppleLLMRuntimeBridge.attachIfPossible()`
 * é chamado no AppDelegate, mas pode completar **DEPOIS** do `App.vue` chamar
 * `auth.init()` (que tenta selecionar o backend via `storageFactory()`).
 *
 * Sem este retry, `getIOSBridge()` retorna null no momento do init() e
 * `storageFactory()` cai em OPFS (que falha em iOS 26+ com sandbox deny).
 *
 * @param opts.timeoutMs - tempo máximo de espera (ms)
 * @param opts.intervalMs - intervalo entre checks (ms)
 *
 * HLOS-172 FIX: timeout padrão 1500ms cobre a janela típica entre AppDelegate
 * didFinishLaunching e a primeira execução JS (em cold start típico: 100-500ms).
 */
export async function waitForIOSBridge(
  opts: { timeoutMs: number; intervalMs: number },
): Promise<void> {
  const start = Date.now();
  while (Date.now() - start < opts.timeoutMs) {
    if (isCapacitorIOSAvailable()) return;
    await new Promise((resolve) => setTimeout(resolve, opts.intervalMs));
  }
  // Timeout — deixa storageFactory() decidir (cairá em OPFS/Memory se aplicável).
  // Não throw — caller pode lidar com vaultExists=false via state="setup".
}
